import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import * as z from 'zod';

import { Toast } from '#components/toast/toast.js';
import { useTRPC } from '#src/trpc.js';
import { useAddToast } from '#state/stores/toast-store.js';
import { UploadSubmitOutcome } from '#utils/analytics/analytics-events.js';
import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';
import { ProjectFileUploader } from './project-file-uploader.js';
import { PROJECT_FORMATS } from './utils/project-format.js';
import { uploadCompleteDetail } from './utils/upload-complete-detail.js';

import type { DeclarableCadTool } from '@cadstrom/sigma-engine-shared';
import type { NameOutcome } from './utils/upload-complete-detail.js';

export const Route = createFileRoute(
	'/_authorization-gate/orgs/$orgName/projects/$projectId/revisions/$revisionId/upload',
)({
	validateSearch: z.object({
		tool: z.enum(PROJECT_FORMATS).optional(),
	}),
	staticData: {
		authorize: (ability) => ability.can('manage', 'project'),
	},
	component: RevFileUploadPage,
});

function RevFileUploadPage() {
	const { projectId, revisionId } = Route.useParams();
	const { tool } = Route.useSearch();
	const navigate = Route.useNavigate();
	const addToast = useAddToast();
	const trpc = useTRPC();
	const queryClient = useQueryClient();

	const { mutateAsync: notifyUploadComplete, isPending: isSubmitting } = useMutation(
		trpc.revision.fileUploadComplete.mutationOptions(),
	);
	const { mutateAsync: updateProject } = useMutation(trpc.project.update.mutationOptions());

	const handleUploadComplete = async (declaredTool: DeclarableCadTool | null, projectName: string | null) => {
		// Named here rather than while the user was still choosing, so an abandoned upload leaves
		// the project as it was created instead of half-renamed. A name that does not land must not
		// block finalizing an upload that already succeeded, so it is reported alongside it instead.
		let nameOutcome: NameOutcome = { kind: 'asRequested' };
		if (projectName !== null) {
			try {
				const { project } = await updateProject({
					id: projectId,
					data: { name: projectName },
					ensureUniqueName: true,
				});
				if (project.name !== projectName) {
					nameOutcome = { kind: 'adjusted', savedAs: project.name };
				}
			} catch (error) {
				console.error('[UploadPage] Failed to name the project', error);
				nameOutcome = { kind: 'failed' };
			}
		}

		// Await finalization before navigating: leaving early unmounts this page and cancels the
		// mutation before the cache invalidation below can run.
		// A thrown error means the request never reached a verdict (offline, revision gone,
		// already finalized); `success: false` is the backend rejecting the file set itself.
		// The two need different recovery advice.
		let outcome: UploadSubmitOutcome;
		let errorMessage: string | undefined;
		try {
			const result = await notifyUploadComplete({
				id: revisionId,
				source: 'projectBuild',
				declaredTool: declaredTool ?? undefined,
			});
			outcome = result.success ? UploadSubmitOutcome.Finalized : UploadSubmitOutcome.Rejected;
		} catch (error) {
			console.error('[UploadPage] fileUploadComplete failed', error);
			outcome = UploadSubmitOutcome.Failed;
			errorMessage = error instanceof Error ? error.message : String(error);
		}

		// Invalidate regardless of outcome: a failed finalize can still change the status (e.g. Error).
		await Promise.all([
			queryClient.invalidateQueries({ queryKey: trpc.revision.list.queryKey() }),
			queryClient.invalidateQueries({ queryKey: trpc.project.list.queryKey() }),
			queryClient.invalidateQueries({ queryKey: trpc.project.getPreview.queryKey({ id: projectId }) }),
			queryClient.invalidateQueries({ queryKey: trpc.revision.get.queryKey({ id: revisionId }) }),
			// A submitted revision holds a design month, which can leave no room for a new project.
			queryClient.invalidateQueries({ queryKey: trpc.billing.getNewProjectDesignMonthGate.queryKey() }),
		]);

		if (outcome !== UploadSubmitOutcome.Finalized) {
			const detail =
				outcome === UploadSubmitOutcome.Rejected ?
					'Validation failed. Review the files and try again.'
				:	'Something went wrong finalizing this upload. Please try again.';
			addToast(
				<Toast severity='error' title='Upload could not be finalized'>
					<span>{detail}</span>
				</Toast>,
			);
			return { outcome, errorMessage };
		}

		addToast(
			<Toast severity='success' title='Upload complete'>
				<span>{uploadCompleteDetail(nameOutcome)}</span>
			</Toast>,
		);
		// Land on the design explorer + workbench while the new revision processes.
		void navigate({ to: '../results', search: { tab: 'workbench', leftTab: 'design-explorer' } });
		return { outcome };
	};

	const el = useDataElTagGenerator(RevFileUploadPage);
	return (
		<div {...el()} className='flex min-h-0 grow basis-0 flex-col items-center p-2 pt-0'>
			<ProjectFileUploader
				projectId={projectId}
				buildId={revisionId}
				initialFormat={tool}
				onUploadComplete={handleUploadComplete}
				isSubmitting={isSubmitting}
			/>
		</div>
	);
}
